import type { CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import type { ISODate } from '@app/shared'

import { useToggleLearningItem, type LearningTrackSummary } from '../../lib/data'
import { PacePill, QuietPill } from './Chrome'
import { activityPhrase, hueFor, RING_CIRCUMFERENCE, ringOffset, scheduleNote } from './derive'
import { ArrowRight, Flame } from './icons'

// ============================================================================
// ONE TRACK CARD — docs/design/handoff/"LifeOS Learning.dc.html", option 9a.
//
// White, radius 16, 1px `line`, shadow-card, padding 18/20. Five stacked blocks: the ring and the
// name, today's item as a real checkbox, the week strip, the NEXT line, and the footer.
//
// TODAY'S ITEM IS THE NEXT UNFINISHED ONE, not the one the calendar dates today. Algebra is two
// days ahead, so the item the plan put on today is already ticked; offering it again would be the
// app forgetting what it was told this morning. The data contract makes that call
// (LearningTrackSummary.nextItem) — this component only draws it.
//
// AND IT IS CHECKABLE IN PLACE. The checkbox writes through useToggleLearningItem, exactly the way
// the Today screen's does, so the percentage, the pace phrase, the week count and the heatmap all
// move together on the next render. Nothing here is a picture of a checkbox.
//
// THE RING is drawn as the mockup draws it. docs/design/FIDELITY.md records "count pill, never a
// ring" as a ratified deviation, and the track page (9b) obeys it — its progress is the pill. On
// the overview the rendered mockup shows a 72px donut per card and the internal-precedence rule
// gives the rendered mockup layout and structure, so it is here, with the count spelled out in
// the footer beside it ("46 of 182 items") so the percentage is never the only claim made.
// ============================================================================

export function TrackCard({
  summary,
  index,
  today,
}: {
  summary: LearningTrackSummary
  /** Which card this is, left to right. Decides the hue; nothing else depends on order. */
  index: number
  today: ISODate
}) {
  const hue = hueFor(index)
  const { track, nextItem, followingItem } = summary
  const toggle = useToggleLearningItem()

  return (
    <section className="flex min-h-0 flex-col gap-[15px] rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
      <header className="flex flex-none items-center gap-4">
        <ProgressRing percent={summary.percent} accent={hue.accent} />
        <div className="flex min-w-0 flex-1 flex-col gap-[7px]">
          <h2 className="truncate text-[16px] font-semibold leading-[1.2] tracking-[-0.01em]">
            {track.name}
          </h2>
          <p className="truncate text-[12px] font-normal leading-none text-muted">
            {track.subtitle}
          </p>
          <div className="flex flex-wrap items-center gap-[7px]">
            <QuietPill fill={hue.fill} text={hue.text} strong>
              {summary.currentUnit}
            </QuietPill>
            <PacePill pace={summary.pace} />
            <QuietPill
              icon={
                summary.streakDays > 0 ? <Flame size={10} className="text-orange-2" /> : undefined
              }
            >
              {activityPhrase(summary, today)}
            </QuietPill>
          </div>
        </div>
      </header>

      {nextItem ? (
        <label
          style={{ '--mo-rule': hue.rule } as CSSProperties}
          className="mo-rule flex flex-none cursor-pointer items-center gap-[11px] rounded-banner border border-fill bg-surface-2 px-[14px] py-3"
        >
          <input
            type="checkbox"
            checked={nextItem.done}
            onChange={(e) => toggle.mutate({ id: nextItem.id, done: e.target.checked })}
            className="peer sr-only"
          />
          <span
            className={[
              'flex size-[19px] flex-none items-center justify-center rounded-keycap border-[1.8px]',
              hue.border,
            ].join(' ')}
            aria-hidden
          />
          <span className="min-w-0 flex-1">
            <span className="mb-[5px] block text-[9.5px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
              Today’s item
            </span>
            <span className="block truncate text-[13px] font-medium leading-[1.3]">
              {nextItem.title}
            </span>
          </span>
          {/* An ESTIMATE, and labelled as one with the tilde. It is this track's own average
              minutes per finished item — a personal baseline, never a population norm. Absent
              entirely until something has been logged, rather than shown as "~0 min". */}
          {summary.estimateMinutes !== null && (
            <span
              title={`This track’s own average so far: ${summary.estimateMinutes} minutes per item. Not a target.`}
              className="flex-none text-micro font-normal text-microcopy"
            >
              ~{summary.estimateMinutes} min
            </span>
          )}
        </label>
      ) : (
        <p className="flex-none rounded-banner border border-fill bg-surface-2 px-[14px] py-3 text-[12px] leading-[1.45] text-ink-3">
          Every item in this plan is finished. There is nothing left to check off.
        </p>
      )}

      <div className="flex flex-none flex-col gap-[9px]">
        <div className="flex items-center gap-[9px]">
          <h3 className="text-[9.5px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
            This week
          </h3>
          <span className="text-micro font-medium text-ink-2">
            {summary.weekDone} {summary.weekDone === 1 ? 'item' : 'items'} done
          </span>
          <span className="text-micro font-normal text-microcopy">
            of {summary.weekPlanned} planned
          </span>
        </div>
        <div className="flex items-center gap-[5px]" aria-hidden>
          {summary.week.map((day, i) => (
            <div key={day.date} className="flex flex-1 flex-col items-center gap-[5px]">
              <span
                className={['h-[6px] w-full rounded-[3px]', day.items > 0 ? hue.bar : 'bg-fill'].join(
                  ' ',
                )}
              />
              <span className="text-[9px] font-normal leading-none text-faintest-2">
                {WEEK_INITIALS[i]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* The NEXT line, and the one place a slipped plan shows its teeth: an item the plan dated
          in the past reads "overdue since Mon" rather than a cheerful weekday. */}
      <div className="flex flex-none items-center gap-[9px] border-t border-line-5 pt-3">
        <h3 className="text-[9.5px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
          Next
        </h3>
        <p className="min-w-0 flex-1 truncate text-[12px] font-normal leading-[1.3] text-ink-3">
          {followingItem
            ? `${followingItem.title} · ${scheduleNote(followingItem.plannedDate, today)}`
            : 'Nothing after this one — the plan ends here.'}
        </p>
      </div>

      {/* Not pinned to the bottom: the mockup lets the card end where its content ends and leaves
          the slack below, so two cards of different lengths still line up block for block. */}
      <div className="flex flex-none items-center gap-[9px]">
        <Link
          to={`/learning/${track.id}`}
          className={[
            'flex items-center gap-[9px] text-label font-semibold underline-offset-2 transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:underline',
            hue.text,
          ].join(' ')}
        >
          Open track
          <ArrowRight size={12} className={hue.accent} />
        </Link>
        <span className="ml-auto text-micro font-normal text-microcopy">
          {track.itemsDone} of {track.itemsTotal} items
        </span>
      </div>
    </section>
  )
}

const WEEK_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

/**
 * The 72px donut: r=30, 8px stroke, `fill` track, the hue over it, the percentage in the middle.
 * `aria-hidden` because the same number is written in words in the footer — a screen reader
 * hearing "25%" twice learns nothing the second time.
 */
function ProgressRing({ percent, accent }: { percent: number; accent: string }) {
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" className="flex-none" aria-hidden>
      <circle cx="36" cy="36" r="30" fill="none" strokeWidth="8" className="stroke-fill" />
      <circle
        cx="36"
        cy="36"
        r="30"
        fill="none"
        stroke="currentColor"
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={ringOffset(percent)}
        transform="rotate(-90 36 36)"
        className={accent}
      />
      <text
        x="36"
        y="40"
        textAnchor="middle"
        className="fill-ink text-[17px] font-semibold"
        style={{ fontFamily: 'inherit' }}
      >
        {percent}%
      </text>
    </svg>
  )
}

/** The card's skeleton, at the final geometry. */
export function TrackCardSkeleton() {
  return (
    <section className="flex min-h-0 flex-col gap-[15px] rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
      <span className="sr-only">Loading…</span>
      <div className="flex items-center gap-4" aria-hidden>
        <div className="size-[72px] flex-none rounded-full border-8 border-fill" />
        <div className="flex flex-1 flex-col gap-[9px]">
          <div className="h-[14px] w-[140px] rounded-full bg-fill" />
          <div className="h-[10px] w-[110px] rounded-full bg-line-3" />
          <div className="h-[16px] w-[220px] rounded-pill bg-line-3" />
        </div>
      </div>
      <div className="h-[54px] rounded-banner bg-surface-2" aria-hidden />
      <div className="h-[6px] rounded-full bg-fill" aria-hidden />
    </section>
  )
}
